import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { Autocomplete, TextField } from '@mui/material';
import { Badge } from 'components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import {
  usePublishingAdminArtists,
  usePublishingAdminItems,
} from 'hooks/swrRequests/usePublishingAdmin';
import type {
  PublishingAdminItem,
  PublishingAdminItemType,
  PublishingRosterArtist,
} from 'types/publishing';
import PaginationBar from 'components/admin/pagination-bar';
import SegmentedTabs, { SegmentedTab } from 'components/admin/segmented-tabs';
import { formatShortDate } from 'utils/helpers';
import { IngestRuns } from './IngestRuns';

const th = 'text-sm font-bold uppercase tracking-[0.04em] text-[#64748b]'; // Collab PlainHeaderCell

const LIMIT = 25;

type AdminTab = 'submissions' | 'runs';

const typeLabel: Record<PublishingAdminItemType, string> = {
  batch: 'Contact us',
  mark: "Didn't write",
  answer: 'Identity',
};
const scopes: { key: PublishingAdminItemType | undefined; label: string }[] = [
  { key: undefined, label: 'All' },
  ...(Object.keys(typeLabel) as PublishingAdminItemType[]).map((key) => ({
    key,
    label: typeLabel[key],
  })),
];
const typeClass: Record<PublishingAdminItemType, string> = {
  batch: 'border-transparent bg-blue-100 text-blue-800 hover:bg-blue-100',
  mark: 'border-transparent bg-gray-100 text-gray-700 hover:bg-gray-100',
  answer: 'border-transparent bg-amber-100 text-amber-800 hover:bg-amber-100',
};
const identityClass: Record<string, string> = {
  usable: 'border-transparent bg-green-100 text-green-800 hover:bg-green-100',
  ambiguous: 'border-transparent bg-amber-100 text-amber-800 hover:bg-amber-100',
  not_found: 'border-transparent bg-red-100 text-red-800 hover:bg-red-100',
};

function Details({ item }: { item: PublishingAdminItem }) {
  if (item.type === 'answer') {
    return (
      <div className="text-base">
        <span className="font-medium">
          {item.answer === 'thats_me' ? "That's me" : "That's not me"}
        </span>
        {item.candidateName && <span className="text-gray-500"> · {item.candidateName}</span>}
        {item.registeredName && (
          <div className="tabular-nums">
            New details:{' '}
            {[item.registeredName, item.society, item.providedIpi || 'no songwriter ID']
              .filter(Boolean)
              .join(' · ')}
          </div>
        )}
      </div>
    );
  }
  return (
    <ul className="m-0 list-none p-0">
      {item.tracks.map((t) => (
        <li key={t.isrc} className="flex flex-wrap items-center gap-2 py-0.5 text-base">
          <span className={t.notMine ? 'line-through text-gray-400' : ''}>{t.title}</span>
          <span className="text-sm tabular-nums text-gray-500">{t.isrc}</span>
          {t.spotifyUrl && (
            <a
              href={t.spotifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-700 underline underline-offset-2"
            >
              Spotify <ExternalLink size={14} />
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

const PublishingAdmin: React.FC = () => {
  const [tab, setTab] = useState<AdminTab>('submissions');
  const [page, setPage] = useState(1);
  const [type, setType] = useState<PublishingAdminItemType | undefined>();
  const [artistId, setArtistId] = useState<string | undefined>();
  const { items, pagination, isLoading, error } = usePublishingAdminItems({
    page,
    limit: LIMIT,
    type,
    spotifyArtistId: artistId,
  });
  const { artists } = usePublishingAdminArtists(true);
  const artist = artists.find((a) => a.spotifyArtistId === artistId) || null;
  const totalPages = pagination?.totalPages || 1;
  const tabs: SegmentedTab<AdminTab>[] = [
    { key: 'submissions', label: 'Submissions', count: pagination?.totalItems },
    { key: 'runs', label: 'Ingest runs' },
  ];

  return (
    <div className="box-border w-full px-6 py-5">
      <div className="overflow-hidden rounded-lg border border-[#e2e8f0]">
        <div className="border-b border-[#e2e8f0] px-5 py-4">
          <SegmentedTabs tabs={tabs} active={tab} onChange={setTab} />
        </div>
        {tab === 'submissions' && (
          <div className="flex flex-wrap items-center gap-3 border-b border-[#e2e8f0] bg-[#FAFBFC] px-5 py-3">
            <div className="inline-flex overflow-hidden rounded-md border border-[#e2e8f0] bg-white">
              {scopes.map((s, i) => {
                const active = s.key === type;
                return (
                  <button
                    key={s.label}
                    type="button"
                    onClick={() => {
                      setType(s.key);
                      setPage(1);
                    }}
                    className={`px-4 py-1.5 text-sm ${i > 0 ? 'border-l border-[#e2e8f0]' : ''} ${
                      active
                        ? 'bg-[#f1f2f4] font-bold text-[#0f172a]'
                        : 'bg-transparent text-[#64748b]'
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
            <Autocomplete<PublishingRosterArtist>
              className="ml-auto w-[280px]"
              options={artists}
              value={artist}
              onChange={(_, picked) => {
                setArtistId(picked?.spotifyArtistId);
                setPage(1);
              }}
              getOptionLabel={(a) => a.artistName}
              isOptionEqualToValue={(o, v) => o.spotifyArtistId === v.spotifyArtistId}
              renderOption={(props, a) => (
                // Key by id: MUI keys by label, and same-name artists (different Spotify ids) collide.
                <li {...props} key={a.spotifyArtistId}>
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{a.artistName}</span>
                    <span className="truncate text-xs text-gray-500">{a.spotifyArtistId}</span>
                  </span>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} size="small" placeholder="All artists" />
              )}
            />
          </div>
        )}

        {tab === 'runs' && <IngestRuns />}
        {tab === 'submissions' && (
          <>
            {error && <p className="m-0 px-5 py-3 text-base text-red-600">{error.message}</p>}
            <div className="overflow-x-auto">
              <Table className="text-base">
                <TableHeader>
                  <TableRow>
                    <TableHead className={`whitespace-nowrap ${th}`}>Date</TableHead>
                    <TableHead className={th}>Type</TableHead>
                    <TableHead className={th}>Artist</TableHead>
                    <TableHead className={th}>Details</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-base text-gray-500">
                        Loading…
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading && !items.length && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-base text-gray-500">
                        Nothing here yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {items.map((item) => (
                    <TableRow key={`${item.type}-${item.id}`} className="align-top">
                      <TableCell className="whitespace-nowrap text-base text-gray-600">
                        {formatShortDate(item.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-sm ${typeClass[item.type]}`}>
                          {typeLabel[item.type]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-lg font-medium text-gray-900">
                          {item.artist.artistName || item.artist.spotifyArtistId}
                        </div>
                        <div className="text-sm text-gray-500">{item.artist.legalName || '—'}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-gray-500">
                          <span className="tabular-nums">{item.artist.ipi || 'no IPI'}</span>
                          {item.artist.hitCount != null && (
                            <span>· {item.artist.hitCount} hits</span>
                          )}
                          {item.artist.identity && (
                            <Badge className={`text-sm ${identityClass[item.artist.identity]}`}>
                              {item.artist.identity}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Details item={item} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <Link
                          to={`/publishing?artist=${item.artist.spotifyArtistId}`}
                          className="text-sm text-blue-700 underline underline-offset-2"
                        >
                          Open tab
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <PaginationBar
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                alwaysShow
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PublishingAdmin;
